import { useMemo, useState } from "react";
import {
  Box,
  Chip,
  IconButton,
  InputAdornment,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import { useNavigate } from "react-router-dom";
import MainHeading from "../components/main-heading.component";
import { COLORS } from "../theme/theme.config";

type HistoryTopic = {
  title: string;
  category: string;
  created: string;
  lastUsed: string;
  creatives: number;
};

type HistoryMonth = {
  month: string;
  count: number;
  topics: HistoryTopic[];
};

const historyMonths: HistoryMonth[] = [
  {
    month: "June 2026",
    count: 41,
    topics: [
      {
        title: "Why Promotions Are Slowing Down",
        category: "Career",
        created: "Jun 14",
        lastUsed: "2 hrs ago",
        creatives: 4,
      },
      {
        title: "AI Agents Will Replace Managers",
        category: "AI",
        created: "Jun 14",
        lastUsed: "Today",
        creatives: 4,
      },
      {
        title: "Salary Growth Myths",
        category: "Finance",
        created: "Jun 13",
        lastUsed: "Yesterday",
        creatives: 8,
      },
    ],
  },
  {
    month: "May 2026",
    count: 58,
    topics: [
      {
        title: "High Income Skills For 2026",
        category: "Career",
        created: "May 28",
        lastUsed: "Jun 10",
        creatives: 4,
      },
      {
        title: "Future Of Remote Work",
        category: "Career",
        created: "May 22",
        lastUsed: "Jun 2",
        creatives: 4,
      },
    ],
  },
];

const historyStats = {
  topics: 127,
  creatives: 642,
};

const surfaceSx = {
  backgroundColor: COLORS.white,
  border: "1px solid rgba(1, 6, 36, 0.08)",
  borderRadius: 2,
  boxShadow: "0 1px 3px rgba(1, 6, 36, 0.06)",
};

const filterMonths = (months: HistoryMonth[], search: string) => {
  const term = search.trim().toLowerCase();
  if (!term) return months;

  return months
    .map((group) => ({
      ...group,
      topics: group.topics.filter((topic) =>
        `${topic.title} ${topic.category} ${topic.created} ${topic.lastUsed}`.toLowerCase().includes(term)
      ),
    }))
    .filter((group) => group.topics.length > 0);
};

const HistoryTable = ({ topics, showHeader }: { topics: HistoryTopic[]; showHeader: boolean }) => (
  <Paper
    sx={{
      ...surfaceSx,
      overflow: "hidden",
      borderRadius: 2,
    }}
  >
    {showHeader && (
      <Box
        sx={{
          display: { xs: "none", md: "grid" },
          gridTemplateColumns: "1fr 140px 140px 90px",
          gap: 2,
          px: 2.5,
          py: 2,
          borderBottom: "1px solid rgba(1, 6, 36, 0.08)",
        }}
      >
        {["Topic", "Created", "Last Used", "Creatives"].map((label) => (
          <Typography
            key={label}
            sx={{
              color: COLORS.iconColor,
              fontSize: 12,
              fontWeight: 800,
              textTransform: "uppercase",
            }}
          >
            {label}
          </Typography>
        ))}
      </Box>
    )}

    {topics.map((topic) => (
      <Box
        key={`${topic.title}-${topic.created}`}
        sx={{
          display: { xs: "block", md: "grid" },
          gridTemplateColumns: "1fr 140px 140px 90px",
          gap: 2,
          px: 2.5,
          py: 2.25,
          borderBottom: "1px solid rgba(1, 6, 36, 0.08)",
          cursor: "pointer",
          transition: "background-color 0.2s ease",
          "&:last-child": { borderBottom: 0 },
          "&:hover": { backgroundColor: "#f6f8fc" },
        }}
      >
        <Box>
          <Typography sx={{ color: COLORS.primary, fontWeight: 750, mb: 0.5 }}>{topic.title}</Typography>
          <Chip
            label={topic.category}
            size="small"
            sx={{
              height: 22,
              borderRadius: 1,
              bgcolor: "rgba(0, 100, 148, 0.08)",
              color: COLORS.secondary,
              fontSize: 11,
              fontWeight: 700,
            }}
          />
        </Box>
        <Typography sx={{ color: COLORS.primary, fontSize: 14, mt: { xs: 1.25, md: 0 } }}>
          {topic.created}
        </Typography>
        <Typography sx={{ color: COLORS.primary, fontSize: 14, mt: { xs: 0.5, md: 0 } }}>
          {topic.lastUsed}
        </Typography>
        <Typography
          sx={{
            color: COLORS.green,
            fontSize: 14,
            fontWeight: 800,
            textAlign: { xs: "left", md: "right" },
            mt: { xs: 0.5, md: 0 },
          }}
        >
          {topic.creatives}
        </Typography>
      </Box>
    ))}
  </Paper>
);

const LibraryHistory = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");

  const filteredMonths = useMemo(() => filterMonths(historyMonths, search), [search]);

  return (
    <div className="container-fluid">
      <MainHeading title="History" subTitle="All generated topics from the last 60 days." />

      <Box sx={{ width: "100%", maxWidth: 1000, pb: 4 }}>
        <Paper sx={{ ...surfaceSx, p: { xs: 2, md: 3 } }}>
          <Box
            component="button"
            onClick={() => navigate("/library")}
            sx={{
              appearance: "none",
              border: 0,
              background: "transparent",
              color: COLORS.iconColor,
              cursor: "pointer",
              display: "inline-flex",
              alignItems: "center",
              gap: 0.75,
              fontSize: 14,
              fontWeight: 700,
              p: 0,
              mb: 2.5,
              "&:hover": { color: COLORS.green },
            }}
          >
            <ArrowBackIcon sx={{ fontSize: 18 }} />
            Back to Library
          </Box>

          <Typography sx={{ color: COLORS.green, fontSize: 14, fontWeight: 800, mb: 3 }}>
            {historyStats.topics} Topics | {historyStats.creatives} Creatives
          </Typography>

          <TextField
            fullWidth
            autoComplete="off"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Escape") setSearch("");
            }}
            placeholder="Search topics..."
            variant="outlined"
            size="small"
            sx={{
              mb: 3,
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
                backgroundColor: "#f6f8fc",
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: COLORS.iconColor }} />
                </InputAdornment>
              ),
              endAdornment: search ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearch("")} sx={{ p: 0.4, color: COLORS.iconColor, "&:hover": { color: COLORS.primary } }}>
                    <CloseIcon sx={{ fontSize: 15 }} />
                  </IconButton>
                </InputAdornment>
              ) : undefined,
            }}
          />

          <Box sx={{ display: "grid", gap: 3 }}>
            {filteredMonths.map((group, index) => (
              <Box key={group.month}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 2,
                    mb: 1.5,
                  }}
                >
                  <Typography sx={{ color: COLORS.primary, fontSize: 18, fontWeight: 800 }}>
                    {group.month}
                  </Typography>
                  <Typography sx={{ color: COLORS.iconColor, fontSize: 13, whiteSpace: "nowrap" }}>
                    {group.count} Topics
                  </Typography>
                </Box>
                <HistoryTable topics={group.topics} showHeader={index === 0} />
              </Box>
            ))}
          </Box>

          {filteredMonths.length === 0 && (
            <Paper sx={{ p: 3, textAlign: "center", bgcolor: "#f6f8fc", borderRadius: 2 }}>
              <Typography sx={{ color: COLORS.iconColor }}>No history topics match your search.</Typography>
            </Paper>
          )}

          <Paper
            sx={{
              mt: 3.5,
              p: 2,
              border: "1px dashed rgba(1, 6, 36, 0.18)",
              borderRadius: 2,
              boxShadow: "none",
            }}
          >
            <Typography sx={{ color: COLORS.iconColor, fontSize: 14 }}>
              Topics older than 60 days are automatically archived and removed from this history view.
            </Typography>
          </Paper>
        </Paper>
      </Box>
    </div>
  );
};

export default LibraryHistory;
